/* Lovable Buy Box — client behavior */
(function () {
  // Notify the host theme that the cart changed. Themes vary wildly in how
  // they listen, so we broadcast every common event, publish to Dawn-style
  // pub/sub, re-render Section Rendering API targets when present, and
  // patch the cart-count bubble as a last resort. Optionally opens the
  // theme's cart drawer (used by bundle/upsell flows that previously did
  // a hard /cart redirect).
  const notifyThemeCartUpdated = async (opts) => {
    const openDrawer = !!(opts && opts.openDrawer);
    let cartData = null;
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (r.ok) cartData = await r.json();
    } catch (e) { /* noop */ }

    // 1. Native theme pub/sub (Brain Forza / Symmetry-style).
    //    Uses `theme.pubsub.publish('cartUpdate', { source, cart })` which the
    //    header bubble, mini-cart, and cart-drawer all subscribe to. This is
    //    the canonical signal for this theme — no section re-fetch needed.
    try {
      if (window.theme && window.theme.pubsub && typeof window.theme.pubsub.publish === 'function') {
        const evt = (window.theme.pubsub.PUB_SUB_EVENTS && window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate) || 'cartUpdate';
        window.theme.pubsub.publish(evt, { source: 'lovable-buy-box', cart: cartData });
      }
    } catch (e) { /* noop */ }

    // 2. Native cart:refresh (re-renders the drawer in this theme; `open`
    //    controls whether the drawer slides open after the refresh).
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open: openDrawer, source: 'lovable-buy-box', cart: cartData }
      }));
    } catch (e) { /* noop */ }

    // 3. Dawn / Horizon-style pub/sub fallback (other themes).
    try {
      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        const evt = window.PUB_SUB_EVENTS.cartUpdate || window.PUB_SUB_EVENTS.CART_UPDATE || 'cart-update';
        window.publish(evt, { source: 'lovable-buy-box', cartData });
      }
    } catch (e) { /* noop */ }

    // 4. Minimal generic broadcast — only "cart was updated" style events.
    //    We intentionally do NOT dispatch `ajaxProduct:added` / `product:added`
    //    because some themes treat those as "a product form just submitted"
    //    and re-post the form, causing the cart to receive duplicate (or
    //    multiplied) line items.
    const detail = { source: 'lovable-buy-box', cart: cartData };
    ['cart:updated', 'cart:update', 'cart:change'].forEach((name) => {
      try { document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true })); } catch (e) {}
    });

    // 5. Last-resort bubble update for themes without any of the above.
    if (cartData && typeof cartData.item_count === 'number') {
      const bubbleSelectors = [
        '#cart-icon-bubble', '#CartCount', '#CartCount-bubble',
        '[data-cart-count]', '.cart-count-bubble', '.cart-count',
        '.header__cart-count', '.site-header__cart-count', '.js-cart-count'
      ];
      document.querySelectorAll(bubbleSelectors.join(',')).forEach((el) => {
        const countNode = el.querySelector('[data-cart-count-bubble], .cart-count-bubble span, span') || el;
        if (countNode && countNode.children.length === 0) {
          countNode.textContent = String(cartData.item_count);
        }
      });
    }

    // 6. Drawer fallback for themes that don't honor cart:refresh `open`.
    if (openDrawer) {
      const triggerSelectors = [
        '[data-cart-drawer-toggle]',
        '[aria-controls*="cart" i][aria-controls*="drawer" i]',
        'a[href="/cart"][aria-controls]',
        '.js-cart-drawer-toggle'
      ];
      for (const sel of triggerSelectors) {
        const t = document.querySelector(sel);
        if (t) { try { t.click(); } catch (e) {} break; }
      }
    }
  };

  const formatMoney = (cents, format) => {
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format || (window.theme && theme.moneyFormat) || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  };

  const initBuyBox = (root) => {
    if (root.dataset.bbInited === 'true') return;
    root.dataset.bbInited = 'true';
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const money = (cents) => formatMoney(cents, moneyFormat);

    const variantsScript = root.querySelector('[data-variants-json]');
    const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    let currentVariantId = parseInt(root.dataset.variantId, 10);
    let currentVariant = variants.find(v => v.id === currentVariantId) || variants[0] || null;

    const ONCE_MAX = parseInt(root.dataset.onceMax, 10) || 20;
    const SUB_MAX = parseInt(root.dataset.subMax, 10) || 3;
    const ONCE_MIN = 1;
    const SUB_MIN = 1;

    // Buy Once tier discount: 1 → 0%, 2 → 5%, 3+ → 10% (capped)
    const onceDiscountFor = (q) => (q <= 1 ? 0 : q === 2 ? 0.05 : 0.10);

    let qtyOnce = 1;
    let qtySub = 1;
    let tierIdx = 0;
    let purchaseType = root.querySelector('[data-option="subscribe"]') ? 'subscribe' : 'once';

    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    const subPriceEl = $('[data-sub-price]');
    const subStrikeEl = $('[data-sub-strike]');
    const oncePriceEl = $('[data-once-price]');
    const onceStrikeEl = $('[data-once-strike]');
    const savingsPctEl = $('[data-savings-pct]');
    const subSavingsLineEl = $('[data-sub-savings-line]');
    const planSelect = $('[data-selling-plan]');
    const addBtn = $('[data-add-to-cart]');
    const addLabel = $('[data-add-label]');
    const ctaPriceEl = $('[data-cta-price]');
    const statusEl = $('[data-status]');
    const statusLabelEl = $('[data-status-label]');

    const subPlanInfo = () => {
      if (!planSelect) return { type: null, value: 0 };
      const opt = planSelect.options[planSelect.selectedIndex];
      if (!opt) return { type: null, value: 0 };
      return { type: opt.dataset.adjType, value: parseFloat(opt.dataset.adjValue) || 0 };
    };

    const subUnitPrice = () => {
      if (!currentVariant) return 0;
      const { type, value } = subPlanInfo();
      if (type === 'percentage') return Math.round(currentVariant.price * (1 - value / 100));
      if (type === 'fixed_amount') return currentVariant.price - Math.round(value * 100);
      if (type === 'price') return Math.round(value * 100);
      return currentVariant.price;
    };

    const refresh = () => {
      if (!currentVariant) return;
      const unit = currentVariant.price;
      const subUnit = subUnitPrice();
      const savingsPct = unit > 0 ? Math.round((1 - subUnit / unit) * 100) : 0;

      // Subscribe row prices
      if (subPriceEl) subPriceEl.textContent = money(subUnit * qtySub);
      if (subStrikeEl) {
        if (savingsPct > 0) { subStrikeEl.hidden = false; subStrikeEl.textContent = money(unit * qtySub); }
        else { subStrikeEl.hidden = true; }
      }
      if (savingsPctEl) savingsPctEl.textContent = 'Save ' + savingsPct + '%';
      const subPillEl = root.querySelector('[data-sub-pill]');
      if (subPillEl) {
        if (savingsPct > 0) { subPillEl.hidden = false; subPillEl.textContent = 'Save ' + savingsPct + '%'; }
        else { subPillEl.hidden = true; }
      }
      if (subSavingsLineEl) subSavingsLineEl.textContent = 'Save ' + money(unit - subUnit) + ' on every order';

      // Buy Once prices (with capped tier discount)
      const tierDiscount = onceDiscountFor(qtyOnce);
      const onceFull = unit * qtyOnce;
      const onceLine = Math.round(onceFull * (1 - tierDiscount));
      if (oncePriceEl) oncePriceEl.textContent = money(onceLine);
      if (onceStrikeEl) {
        if (tierDiscount > 0) { onceStrikeEl.hidden = false; onceStrikeEl.textContent = money(onceFull); }
        else { onceStrikeEl.hidden = true; }
      }

      // Tier per-button "Save $X" labels.
      // Tier 0 (1 bottle) → empty
      // Tier 1 (2 bottles) → exact $ saved
      // Tier 2 (3+ bottles) → "Save 10% on 3+" so the cap is clear
      $$('[data-tier-save]').forEach(el => {
        const i = parseInt(el.dataset.tierSave, 10);
        if (i === 1) {
          const save = Math.round(unit * 2 * 0.05);
          el.textContent = save > 0 ? 'Save ' + money(save) : '';
        } else if (i === 2) {
          el.textContent = 'Save 10% on 3+';
        } else {
          el.textContent = '';
        }
      });

      // Stepper values + disabled state
      const subVal = $('[data-qty-value="sub"]');
      const onceVal = $('[data-qty-value="once"]');
      if (subVal) subVal.textContent = qtySub;
      if (onceVal) onceVal.textContent = qtyOnce;
      $$('[data-stepper="sub"] .bb__step').forEach(b => {
        const s = parseInt(b.dataset.step, 10);
        b.disabled = (s < 0 && qtySub <= SUB_MIN) || (s > 0 && qtySub >= SUB_MAX);
      });
      $$('[data-stepper="once"] .bb__step').forEach(b => {
        const s = parseInt(b.dataset.step, 10);
        b.disabled = (s < 0 && qtyOnce <= ONCE_MIN) || (s > 0 && qtyOnce >= ONCE_MAX);
      });

      // Stock state — drive from the per-variant Liquid data (inventory_policy /
      // inventory_management / inventory_quantity) so every variant uses the same
      // rule. Pre-order = explicit product tag OR a tracked, continue-selling
      // variant with qty <= 0. Otherwise trust Shopify's `available` flag.
      const forcePreorder = root.dataset.forcePreorder === 'true';
      let inStock = !!currentVariant.available;
      let isPreorder = false;
      if (forcePreorder) {
        inStock = false;
        isPreorder = true;
      } else if (currentVariant.inventory_policy === 'continue'
                 && currentVariant.inventory_management
                 && Number(currentVariant.inventory_quantity ?? 0) <= 0) {
        inStock = false;
        isPreorder = true;
      }
      const purchasable = inStock || isPreorder;

      // CTA
      const linePrice = purchaseType === 'subscribe' ? subUnit * qtySub : onceLine;
      if (purchasable) {
        if (addBtn) addBtn.disabled = false;
        if (ctaPriceEl) ctaPriceEl.textContent = money(linePrice);
        if (addLabel) {
          const base = isPreorder ? 'Pre-order' : (root.dataset.ctaLabel || 'Add to Cart');
          addLabel.innerHTML = base + ' — <span data-cta-price>' + money(linePrice) + '</span>';
        }
      } else {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Sold Out';
      }

      // Status pill
      if (statusEl && statusLabelEl) {
        statusEl.classList.remove('bb__status--in', 'bb__status--pre', 'bb__status--out');
        if (inStock) {
          statusEl.classList.add('bb__status--in');
          statusLabelEl.textContent = 'IN STOCK - READY TO SHIP';
        } else if (isPreorder) {
          statusEl.classList.add('bb__status--pre');
          statusLabelEl.textContent = 'Pre-order now';
        } else {
          statusEl.classList.add('bb__status--out');
          statusLabelEl.textContent = 'Out of stock';
        }
      }
    };

    // Save original CTA label for re-renders
    if (addLabel) {
      const txt = addLabel.textContent.split('—')[0].trim();
      root.dataset.ctaLabel = txt;
    }

    // Variant selectors
    const variantSelects = $$('[data-variant-option]');
    if (variantSelects.length) {
      const updateVariant = () => {
        const chosen = variantSelects.map(s => s.value);
        const match = variants.find(v => v.options.every((o, i) => o === chosen[i]));
        if (match) {
          currentVariant = match;
          currentVariantId = match.id;
          root.dataset.variantId = match.id;
          refresh();
          // Swap gallery to the variant's featured media — try media id,
          // then image id, then matching image URL/path against gallery.
          if (typeof root._bbGalleryGotoVariant === 'function') {
            root._bbGalleryGotoVariant({
              mediaId: match.featured_media && match.featured_media.id,
              imageId: match.featured_image && match.featured_image.id,
              imgSrc: match.featured_image && match.featured_image.src,
            });
          }
        }
      };
      variantSelects.forEach(s => s.addEventListener('change', updateVariant));
    }

    // Custom variant dropdowns wired to hidden <select data-variant-option>
    $$('[data-variant-dd]').forEach((dd) => {
      const btn = dd.querySelector('[data-variant-btn]');
      const menu = dd.querySelector('[data-variant-menu]');
      const current = dd.querySelector('[data-variant-current]');
      const native = dd.querySelector('[data-variant-option]');
      if (!btn || !menu || !native) return;
      const items = Array.from(menu.querySelectorAll('[data-value]'));
      const sync = () => {
        if (current) current.textContent = native.value;
        items.forEach(i => i.classList.toggle('is-active', i.dataset.value === native.value));
      };
      const close = () => { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
      const open = () => { menu.hidden = false; btn.setAttribute('aria-expanded', 'true'); };
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        if (menu.hidden) open(); else close();
      });
      items.forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          native.value = item.dataset.value;
          native.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
          close();
        });
      });
      document.addEventListener('click', (e) => {
        if (!menu.hidden && !dd.contains(e.target)) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !menu.hidden) { close(); btn.focus(); }
      });
      native.addEventListener('change', sync);
      sync();
    });

    // Steppers
    $$('[data-stepper] .bb__step').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const stepper = btn.closest('[data-stepper]');
        const which = stepper.dataset.stepper;
        const delta = parseInt(btn.dataset.step, 10);
        if (which === 'sub') {
          qtySub = Math.max(SUB_MIN, Math.min(SUB_MAX, qtySub + delta));
        } else {
          qtyOnce = Math.max(ONCE_MIN, Math.min(ONCE_MAX, qtyOnce + delta));
          // Sync tier highlight: tier 3 stays active for any qty >= 3 (capped discount)
          tierIdx = Math.min(qtyOnce, 3) - 1;
          $$('.bb__tier').forEach(t => t.classList.toggle('is-active', parseInt(t.dataset.tier, 10) === tierIdx));
        }
        refresh();
      });
    });

    // Tier picker
    $$('.bb__tier').forEach(t => {
      t.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        $$('.bb__tier').forEach(x => x.classList.remove('is-active'));
        t.classList.add('is-active');
        tierIdx = parseInt(t.dataset.tier, 10);
        qtyOnce = parseInt(t.dataset.qty, 10);
        refresh();
      });
    });

    // Purchase option toggle
    $$('.bb__option').forEach(opt => {
      opt.addEventListener('click', (e) => {
        if (e.target.closest('select') || e.target.closest('[data-stepper]') || e.target.closest('.bb__tier')) return;
        $$('.bb__option').forEach(o => o.classList.remove('is-active'));
        opt.classList.add('is-active');
        const radio = opt.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        purchaseType = opt.dataset.option;
        refresh();
      });
    });

    if (planSelect) planSelect.addEventListener('change', refresh);

    // Custom frequency dropdown wired to hidden <select>
    const freqDD = $('[data-freq-dd]');
    const freqBtn = $('[data-freq-btn]');
    const freqMenu = $('[data-freq-menu]');
    const freqCurrent = $('[data-freq-current]');
    if (freqDD && freqBtn && freqMenu && planSelect) {
      const items = Array.from(freqMenu.querySelectorAll('[data-value]'));
      const labelFor = (val) => {
        const it = items.find(i => i.dataset.value === String(val));
        return it ? it.textContent.trim() : '';
      };
      const syncLabel = () => {
        if (freqCurrent) freqCurrent.textContent = labelFor(planSelect.value);
        items.forEach(i => i.classList.toggle('is-active', i.dataset.value === String(planSelect.value)));
      };
      const closeMenu = () => { freqMenu.hidden = true; freqBtn.setAttribute('aria-expanded', 'false'); };
      const openMenu = () => { freqMenu.hidden = false; freqBtn.setAttribute('aria-expanded', 'true'); };
      syncLabel();
      freqBtn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        if (freqMenu.hidden) openMenu(); else closeMenu();
      });
      items.forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          planSelect.value = item.dataset.value;
          planSelect.dispatchEvent(new Event('change', { bubbles: true }));
          syncLabel();
          closeMenu();
        });
      });
      document.addEventListener('click', (e) => {
        if (!freqMenu.hidden && !freqDD.contains(e.target)) closeMenu();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !freqMenu.hidden) { closeMenu(); freqBtn.focus(); }
      });
      planSelect.addEventListener('change', syncLabel);
    }

    // Add to cart
    let isAdding = false;
    if (addBtn) {
      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isAdding) return;
        if (!currentVariant) return;
        isAdding = true;
        const qty = purchaseType === 'subscribe' ? qtySub : qtyOnce;
        const body = { id: currentVariant.id, quantity: qty };
        if (purchaseType === 'subscribe' && planSelect) {
          body.selling_plan = parseInt(planSelect.value, 10);
        }
        const originalHtml = addLabel ? addLabel.innerHTML : '';
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Adding…';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body)
          });
          if (!res.ok) throw new Error(await res.text());
          if (addLabel) addLabel.textContent = 'Added ✓';
          await notifyThemeCartUpdated({ openDrawer: false });
          setTimeout(() => {
            if (addLabel) addLabel.innerHTML = originalHtml;
            addBtn.disabled = false;
            isAdding = false;
            refresh();
          }, 1400);
        } catch (err) {
          console.error('[lovable-buy-box] add to cart failed', err);
          if (addLabel) addLabel.textContent = 'Try again';
          addBtn.disabled = false;
          isAdding = false;
        }
      });
    }

    // Gallery
    const gallery = root.querySelector('[data-gallery]');
    if (gallery) {
      const mediaEls = Array.from(gallery.querySelectorAll('.bb__media'));
      const thumbEls = Array.from(gallery.querySelectorAll('.bb__thumb'));

      // Ensure inline-video autoplay on iOS/Safari and pause non-active videos.
      mediaEls.forEach((m) => {
        const v = m.querySelector('video');
        if (v) {
          v.muted = true;
          v.defaultMuted = true;
          v.setAttribute('muted', '');
          v.setAttribute('playsinline', '');
          v.setAttribute('webkit-playsinline', '');
          v.loop = true;
          if (!m.classList.contains('is-active')) v.pause();
        }
      });

      // Build mobile dot indicators (one per media item)
      const mainEl = gallery.querySelector('.bb__main');
      const trackEl = gallery.querySelector('[data-track]');
      let dotsEl = null;
      let dotEls = [];
      const mql = window.matchMedia('(max-width: 749px)');
      const isMobile = () => mql.matches;

      const setActive = (i, opts) => {
        const fromScroll = opts && opts.fromScroll;
        mediaEls.forEach((m, idx) => {
          const active = idx === i;
          m.classList.toggle('is-active', active);
          const v = m.querySelector('video');
          if (v) {
            if (active) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
            else { v.pause(); }
          }
        });
        // Desktop: slide the track via transform. Mobile: native scroll-snap (handled below).
        if (trackEl && !isMobile()) {
          trackEl.style.transform = 'translateX(-' + (i * 100) + '%)';
        }
        thumbEls.forEach((t, idx) => {
          const active = idx === i;
          t.classList.toggle('is-active', active);
          if (active && t.scrollIntoView) {
            try { t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }); } catch (_) {}
          }
        });
        dotEls.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
        if (!fromScroll && isMobile() && mainEl) {
          const target = i * mainEl.clientWidth;
          if (Math.abs(mainEl.scrollLeft - target) > 2) {
            mainEl.scrollTo({ left: target, behavior: 'smooth' });
          }
        }
      };

      // Expose a variant-aware goto for variant changes. Prefers Shopify's
      // media id; falls back to matching the variant's featured image URL
      // against each gallery media's <img src> (ignoring size suffixes).
      const stripImg = (u) => {
        if (!u) return '';
        try {
          const url = new URL(u, window.location.origin);
          // Drop query (size/crop variants) and the trailing _NxN size token.
          const path = url.pathname.replace(/_(\d+x\d+|\d+x|x\d+)(?=\.[a-z]+$)/i, '');
          return path;
        } catch (_) { return String(u); }
      };
      root._bbGalleryGotoVariant = ({ mediaId, imageId, imgSrc } = {}) => {
        if (mediaId != null) {
          const target = String(mediaId);
          const idx = mediaEls.findIndex((m) => String(m.dataset.mediaId) === target);
          if (idx >= 0) { setActive(idx); return; }
        }
        if (imageId != null) {
          const target = String(imageId);
          const idx = mediaEls.findIndex((m) => String(m.dataset.imageId || '') === target);
          if (idx >= 0) { setActive(idx); return; }
        }
        if (imgSrc) {
          const target = stripImg(imgSrc);
          // Try data-media-src first (preview image URL set by Liquid)
          let idx = mediaEls.findIndex((m) => {
            const src = m.dataset.mediaSrc;
            return src && stripImg(src) === target;
          });
          if (idx < 0) {
            idx = mediaEls.findIndex((m) => {
              const img = m.querySelector('img');
              return img && stripImg(img.getAttribute('src')) === target;
            });
          }
          if (idx >= 0) setActive(idx);
        }
      };
      // Back-compat alias.
      root._bbGalleryGotoMedia = (mediaId) => root._bbGalleryGotoVariant({ mediaId });

      if (mainEl && mediaEls.length > 1) {
        dotsEl = document.createElement('div');
        dotsEl.className = 'bb__dots';
        dotsEl.setAttribute('role', 'tablist');
        dotsEl.setAttribute('aria-label', 'Gallery navigation');
        mediaEls.forEach((_, idx) => {
          const d = document.createElement('button');
          d.type = 'button';
          d.className = 'bb__dot' + (idx === 0 ? ' is-active' : '');
          d.setAttribute('aria-label', 'Show media ' + (idx + 1));
          d.addEventListener('click', () => setActive(idx));
          dotsEl.appendChild(d);
          dotEls.push(d);
        });
        mainEl.insertAdjacentElement('afterend', dotsEl);
      }

      thumbEls.forEach((t, i) => t.addEventListener('click', () => setActive(i)));

      // Mobile: derive active index from scroll position (native swipe with momentum)
      if (mainEl && mediaEls.length > 1) {
        let rafId = 0;
        const onScroll = () => {
          if (!isMobile()) return;
          if (rafId) return;
          rafId = requestAnimationFrame(() => {
            rafId = 0;
            const w = mainEl.clientWidth;
            if (!w) return;
            const i = Math.round(mainEl.scrollLeft / w);
            const cur = mediaEls.findIndex((m) => m.classList.contains('is-active'));
            if (i !== cur && i >= 0 && i < mediaEls.length) {
              setActive(i, { fromScroll: true });
            }
          });
        };
        mainEl.addEventListener('scroll', onScroll, { passive: true });
        // Re-align on viewport changes / orientation
        const realign = () => {
          const cur = Math.max(0, mediaEls.findIndex((m) => m.classList.contains('is-active')));
          if (isMobile()) {
            if (trackEl) trackEl.style.transform = '';
            mainEl.scrollLeft = cur * mainEl.clientWidth;
          } else if (trackEl) {
            trackEl.style.transform = 'translateX(-' + (cur * 100) + '%)';
          }
        };
        window.addEventListener('resize', realign);
        if (mql.addEventListener) mql.addEventListener('change', realign);
      }

      // Kick off autoplay for whichever media is initially active.
      const initial = mediaEls.findIndex((m) => m.classList.contains('is-active'));
      if (initial >= 0) {
        const v = mediaEls[initial].querySelector('video');
        if (v) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      }

      // Lightbox (images only)
      const lightboxEnabled = gallery.dataset.lightbox === 'true';
      const lightbox = root.querySelector('[data-lightbox-root]');
      if (lightboxEnabled && lightbox) {
        const lbImg = lightbox.querySelector('[data-lightbox-img]');
        const imageMediaEls = mediaEls.filter((m) => (m.dataset.mediaType || 'image') === 'image');
        let lbIndex = 0;
        const open = (i) => {
          lbIndex = i;
          const img = imageMediaEls[i] && imageMediaEls[i].querySelector('img');
          if (img) lbImg.src = img.src;
          lightbox.hidden = false;
          document.body.style.overflow = 'hidden';
        };
        const close = () => { lightbox.hidden = true; document.body.style.overflow = ''; };
        const nav = (delta) => {
          if (!imageMediaEls.length) return;
          lbIndex = (lbIndex + delta + imageMediaEls.length) % imageMediaEls.length;
          const img = imageMediaEls[lbIndex].querySelector('img');
          if (img) lbImg.src = img.src;
        };
        mediaEls.forEach((m) => m.addEventListener('click', (e) => {
          const type = m.dataset.mediaType || 'image';
          if (type !== 'image') return; // don't hijack video clicks
          e.preventDefault();
          const idx = imageMediaEls.indexOf(m);
          if (idx >= 0) open(idx);
        }));
        lightbox.querySelector('[data-lightbox-close]').addEventListener('click', close);
        lightbox.querySelector('[data-lightbox-prev]').addEventListener('click', () => nav(-1));
        lightbox.querySelector('[data-lightbox-next]').addEventListener('click', () => nav(1));
        lightbox.addEventListener('click', (e) => { if (e.target === lightbox) close(); });
        document.addEventListener('keydown', (e) => {
          if (lightbox.hidden) return;
          if (e.key === 'Escape') close();
          if (e.key === 'ArrowLeft') nav(-1);
          if (e.key === 'ArrowRight') nav(1);
        });
      }
    }

    // ===== Frequently bought together =====
    const bundle = root.querySelector('[data-bundle]');
    if (bundle) {
      const tiers = {
        2: (parseFloat(bundle.dataset.discount2) || 0) / 100,
        3: (parseFloat(bundle.dataset.discount3) || 0) / 100,
      };
      const discountFor = (n) => {
        let best = 0;
        for (const k of Object.keys(tiers)) {
          const tk = parseInt(k, 10);
          if (tk <= n && tiers[tk] > best) best = tiers[tk];
        }
        return best;
      };

      const buildCardHTML = (item) => {
        const img = item.image
          ? `<img src="${item.image}" alt="${(item.title || '').replace(/"/g, '&quot;')}" loading="lazy" />`
          : '';
        return `
          <span class="bb__bundle-plus" aria-hidden="true">+</span>
          <label class="bb__bundle-card">
            <input type="checkbox" checked data-bundle-item data-variant-id="${item.variantId}" data-price="${item.price}" />
            <span class="bb__bundle-check" aria-hidden="true"></span>
            <span class="bb__bundle-thumb">${img}</span>
            <span class="bb__bundle-name">${item.title}</span>
            <span class="bb__bundle-price">${item.priceFormatted || money(item.price)}</span>
          </label>
        `;
      };

      const normalizeRecProduct = (p) => {
        if (!p || !p.variants || !p.variants.length) return null;
        const v = p.variants.find((x) => x.available) || p.variants[0];
        if (!v || v.available === false) return null;
        // Shopify product JSON returns price as a string in major units (e.g. "45.00"); convert to cents.
        const priceCents = Math.round(parseFloat(v.price) * 100);
        return {
          id: p.id,
          title: p.title,
          image: (p.featured_image) || (p.images && p.images[0]) || null,
          variantId: v.id,
          price: priceCents,
          priceFormatted: null,
        };
      };

      const populateAuto = async () => {
        if (bundle.dataset.auto !== 'true') return;
        if (bundle.dataset.populated === 'true') return;
        bundle.dataset.populated = 'true';
        const productId = bundle.dataset.productId;
        const anchorVariantId = bundle.querySelector('[data-bundle-anchor] [data-bundle-item]')?.dataset.variantId;
        let companions = [];

        // 1. Shopify recommendations API
        if (productId) {
          try {
            const url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId)
              + '&intent=complementary&limit=3';
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            if (res.ok) {
              const data = await res.json();
              const list = (data.products || [])
                .map(normalizeRecProduct)
                .filter((c) => c && String(c.variantId) !== String(anchorVariantId));
              companions = list.slice(0, 2);
            }
          } catch (e) { /* fall through */ }
        }

        // 2. Same-collection pool (server-rendered)
        if (companions.length < 2) {
          const poolEl = bundle.querySelector('[data-bundle-pool]');
          if (poolEl) {
            try {
              const pool = JSON.parse(poolEl.textContent);
              const seen = new Set(companions.map((c) => String(c.variantId)));
              if (anchorVariantId) seen.add(String(anchorVariantId));
              for (const item of pool) {
                if (companions.length >= 2) break;
                if (seen.has(String(item.variantId))) continue;
                companions.push(item);
                seen.add(String(item.variantId));
              }
            } catch (e) { /* ignore */ }
          }
        }

        if (companions.length === 0) return;

        const row = bundle.querySelector('[data-bundle-row]');
        if (row) {
          row.insertAdjacentHTML('beforeend', companions.map(buildCardHTML).join(''));
        }
        bundle.hidden = false;
      };

      const initBundleControls = () => {
        const items = Array.from(bundle.querySelectorAll('[data-bundle-item]'));
        const totalEl = bundle.querySelector('[data-bundle-total]');
        const strikeEl = bundle.querySelector('[data-bundle-strike]');
        const metaEl = bundle.querySelector('[data-bundle-meta]');
        const countEl = bundle.querySelector('[data-bundle-count]');
        const addBtn = bundle.querySelector('[data-bundle-add]');
        const pillEl = bundle.querySelector('[data-bundle-pill]');
        const pillPctEl = bundle.querySelector('[data-bundle-pill-pct]');

        // If only the anchor remains (no companions found), hide the section.
        if (items.length < 2) {
          bundle.hidden = true;
          return;
        }

        const refreshBundle = () => {
          const selected = items.filter((i) => i.checked);
          const subtotal = selected.reduce((s, i) => s + (parseInt(i.dataset.price, 10) || 0), 0);
          const discount = discountFor(selected.length);
          const hasDiscount = discount > 0;
          const total = hasDiscount ? Math.round(subtotal * (1 - discount)) : subtotal;
          if (totalEl) totalEl.textContent = money(total);
          if (strikeEl) strikeEl.textContent = hasDiscount ? money(subtotal) : '';
          if (metaEl) {
            const base = selected.length + ' item' + (selected.length === 1 ? '' : 's');
            metaEl.innerHTML = hasDiscount
              ? base + ' · <span style="color:#047857">Save ' + money(subtotal - total) + '</span>'
              : base;
          }
          if (countEl) countEl.textContent = selected.length;
          if (addBtn) addBtn.disabled = selected.length === 0;
          if (pillEl) {
            pillEl.hidden = !hasDiscount;
            if (pillPctEl) pillPctEl.textContent = Math.round(discount * 100);
          }
        };
        items.forEach((i) => i.addEventListener('change', refreshBundle));
        refreshBundle();

        if (addBtn) {
          addBtn.addEventListener('click', async () => {
            const selected = items.filter((i) => i.checked);
            if (!selected.length) return;
            addBtn.disabled = true;
            try {
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                  items: selected.map((i) => ({ id: parseInt(i.dataset.variantId, 10), quantity: 1 })),
                }),
              });
              await notifyThemeCartUpdated({ openDrawer: true });
              addBtn.disabled = false;
            } catch (e) {
              console.error('Bundle add failed', e);
              addBtn.disabled = false;
            }
          });
        }

        // Upsell CTA — adds the manually-picked offer in a single tap.
        const upsellBtn = bundle.querySelector('[data-bundle-upsell-add]');
        if (upsellBtn) {
          upsellBtn.addEventListener('click', async () => {
            const vid = parseInt(upsellBtn.dataset.variantId, 10);
            if (!vid) return;
            upsellBtn.disabled = true;
            try {
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
              });
              await notifyThemeCartUpdated({ openDrawer: true });
              upsellBtn.disabled = false;
            } catch (e) {
              console.error('Upsell add failed', e);
              upsellBtn.disabled = false;
            }
          });
        }
      };

      populateAuto().then(initBundleControls).catch(() => initBundleControls());
    }

    // ===== Sticky mobile CTA =====
    const sticky = document.querySelector('[data-bb-sticky]');
    const mainCta = root.querySelector('[data-add-to-cart]');
    if (sticky && mainCta && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      const stickyAdd = sticky.querySelector('[data-sticky-add]');
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      const obs = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0.1 });
      obs.observe(mainCta);
      // Mirror current CTA price into sticky bar
      const ctaPrice = root.querySelector('[data-cta-price]');
      const syncStickyPrice = () => {
        if (ctaPrice && stickyPrice) stickyPrice.textContent = ctaPrice.textContent;
      };
      syncStickyPrice();
      if (ctaPrice) {
        new MutationObserver(syncStickyPrice).observe(ctaPrice, { childList: true, characterData: true, subtree: true });
      }
      if (stickyAdd) stickyAdd.addEventListener('click', () => mainCta.click());
    }

    refresh();
  };

  const boot = () => document.querySelectorAll('[data-buy-box]').forEach(initBuyBox);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-buy-box]');
    if (root) initBuyBox(root);
  });
})();
